import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import Success from './Success';
import RetrieveLicense from './RetrieveLicense';
import Support from './Support';
import './App.css';

const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    'pk_live_51PkCxzCdf82O5H1WDX5jmKPdijNUzXue0K1PRGT2VNYrlnpTz8LW0DpSFXXG9PcAPUQQrUBfjE3NWnus0acv7o1t00VyrfPgT6'
);

const WORKFLOW_VIDEOS = [
    {
        id: 'regex',
        step: '01',
        title: 'Batch Regex Search',
        video: '/videos/batch-regex.mp4',
        caption: 'Run one regular expression across 4 PDFs simultaneously and highlight all 18 dollar amounts in 14 milliseconds.'
    },
    {
        id: 'layers',
        step: '02',
        title: 'Multi-Color Layers',
        video: '/videos/multi-color-layers.mp4',
        caption: 'Stack Coral liability highlights alongside Yellow financial highlights—click any row in the bottom ledger to jump straight to that file and page.'
    },
    {
        id: 'export',
        step: '03',
        title: 'One-Click PDF Export',
        video: '/videos/instant-export.mp4',
        caption: 'Filter the audit ledger by document and export standard quad-point highlighted PDFs ready for any reader.'
    }
];

const SANDBOX_PRESETS = [
    {
        label: 'Dollar Figures',
        pattern: '\\$[0-9,]+(\\.[0-9]{2})?',
        colorName: 'Yellow',
        colorHex: '#facc15',
        bgRgba: 'rgba(250, 204, 21, 0.32)',
        borderHex: '#eab308',
        type: 'money'
    },
    {
        label: 'Liability Clauses',
        pattern: 'Section 14\\.2|Indemnification|Force Majeure',
        colorName: 'Coral',
        colorHex: '#fb7185',
        bgRgba: 'rgba(251, 113, 133, 0.30)',
        borderHex: '#f43f5e',
        type: 'clause'
    },
    {
        label: 'Settlement Terms',
        pattern: 'net-15|upon closing|escrow reserve',
        colorName: 'Emerald',
        colorHex: '#34d399',
        bgRgba: 'rgba(52, 211, 153, 0.28)',
        borderHex: '#10b981',
        type: 'term'
    }
];

function App() {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('regex');
    const [activeSandboxLayers, setActiveSandboxLayers] = useState(['money', 'clause']);

    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
        return <Success />;
    }

    const path = window.location.pathname;
    if (path === '/retrieve' || path === '/retrieve-license') {
        return <RetrieveLicense />;
    }

    if (path === '/support') {
        return <Support />;
    }

    const handlePurchase = async (productId, productName) => {
        setLoading(true);

        try {
            const stripe = await stripePromise;

            const response = await fetch('/api/create-checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    product_id: productId,
                    product_name: productName,
                    success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
                    cancel_url: `${window.location.origin}/cancel`,
                }),
            });

            const data = await response.json();

            if (!response.ok || data.error) {
                alert(`Error: ${data.error || 'Server returned an error'}`);
                setLoading(false);
                return;
            }

            if (!data.session_id) {
                alert('No session ID returned from server');
                setLoading(false);
                return;
            }

            const result = await stripe.redirectToCheckout({
                sessionId: data.session_id,
            });

            if (result.error) {
                alert(result.error.message);
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleSandboxLayer = (layerType) => {
        setActiveSandboxLayers((prev) =>
            prev.includes(layerType)
                ? prev.filter((item) => item !== layerType)
                : [...prev, layerType]
        );
    };

    const currentVideo = WORKFLOW_VIDEOS.find((w) => w.id === activeTab) || WORKFLOW_VIDEOS[0];

    return (
        <div className="bmpdf-site">
            {/* Minimal Navigation */}
            <nav className="bmpdf-nav">
                <div className="bmpdf-nav__inner">
                    <a href="/" className="bmpdf-brand">
                        <img src="/logo.png" alt="BatchMark PDF" className="bmpdf-brand__logo" />
                        <span className="bmpdf-brand__name">BatchMark PDF</span>
                    </a>
                    <div className="bmpdf-nav__links">
                        <a href="#in-action" className="bmpdf-nav__link">See It Work</a>
                        <a href="#pricing" className="bmpdf-nav__link">Pricing</a>
                        <a href="/retrieve" className="bmpdf-nav__link">Retrieve License</a>
                        <a href="/support" className="bmpdf-nav__link">Support</a>
                        <a
                            href="/downloads/BatchMarkPDF-Windows-Setup-v0.1.0.exe"
                            className="bmpdf-nav__cta"
                        >
                            Download Free Trial
                        </a>
                    </div>
                </div>
            </nav>

            {/* Hero Section — Large Typography, Less is More */}
            <header className="bmpdf-hero">
                <div className="bmpdf-container">
                    <div className="bmpdf-eyebrow">
                        NATIVE RUST DESKTOP WORKSTATION • 100% OFFLINE
                    </div>
                    <h1 className="bmpdf-hero__title">
                        Search 100 PDFs.<br />
                        <span className="bmpdf-highlight-sweep">Highlight every match</span> in one click.
                    </h1>
                    <p className="bmpdf-hero__lead">
                        Stop opening files one by one. Run regex and keyword searches across entire folders locally,
                        colour-code clauses in milliseconds, and export clean, annotated PDFs.
                    </p>

                    <div className="bmpdf-hero__actions">
                        <a
                            href="/downloads/BatchMarkPDF-Windows-Setup-v0.1.0.exe"
                            className="bmpdf-btn bmpdf-btn--primary"
                        >
                            <span>Download for Windows</span>
                            <span className="bmpdf-btn__meta">Free Trial • v0.1.0 (9.8 MB)</span>
                        </a>
                        <a href="#pricing" className="bmpdf-btn bmpdf-btn--secondary">
                            <span>Buy Lifetime License</span>
                            <span className="bmpdf-btn__meta">From $99 CAD — No Subscription</span>
                        </a>
                    </div>

                    <div className="bmpdf-trust-strip">
                        <span>Zero Cloud Uploads</span>
                        <span className="bmpdf-dot">•</span>
                        <span>Regex &amp; Built-In OCR</span>
                        <span className="bmpdf-dot">•</span>
                        <span>Multi-Color Audit Table</span>
                        <span className="bmpdf-dot">•</span>
                        <span>Windows 10/11 (64-bit)</span>
                    </div>
                </div>
            </header>

            {/* Recorded Interactions Showcase */}
            <section id="in-action" className="bmpdf-showcase">
                <div className="bmpdf-container">
                    <div className="bmpdf-showcase__header">
                        <h2 className="bmpdf-section-title">See it in action.</h2>
                        <p className="bmpdf-section-subtitle">
                            Watch real recorded workflows or test multi-layer highlighting live below.
                        </p>
                    </div>

                    {/* Workflow Switcher Pills */}
                    <div className="bmpdf-tabs" role="tablist">
                        {WORKFLOW_VIDEOS.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={`bmpdf-tab ${activeTab === item.id ? 'is-active' : ''}`}
                                onClick={() => setActiveTab(item.id)}
                            >
                                <span className="bmpdf-tab__num">{item.step}</span>
                                <span className="bmpdf-tab__label">{item.title}</span>
                            </button>
                        ))}
                        <button
                            type="button"
                            className={`bmpdf-tab bmpdf-tab--interactive ${activeTab === 'sandbox' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('sandbox')}
                        >
                            <span className="bmpdf-tab__num">LIVE</span>
                            <span className="bmpdf-tab__label">Try It Yourself</span>
                        </button>
                    </div>

                    {/* Video Stage or Live Interactive Stage */}
                    {activeTab !== 'sandbox' ? (
                        <div className="bmpdf-stage">
                            <div className="bmpdf-video-frame">
                                <video
                                    key={currentVideo.video}
                                    src={currentVideo.video}
                                    poster="/videos/poster.png"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="bmpdf-video"
                                />
                            </div>
                            <p className="bmpdf-stage__caption">{currentVideo.caption}</p>
                        </div>
                    ) : (
                        <div className="bmpdf-sandbox">
                            <div className="bmpdf-sandbox__controls">
                                <span className="bmpdf-sandbox__prompt">Toggle highlight layers across the loaded audit PDF:</span>
                                <div className="bmpdf-sandbox__pills">
                                    {SANDBOX_PRESETS.map((preset) => {
                                        const isOn = activeSandboxLayers.includes(preset.type);
                                        return (
                                            <button
                                                key={preset.type}
                                                type="button"
                                                onClick={() => toggleSandboxLayer(preset.type)}
                                                className={`bmpdf-layer-pill ${isOn ? 'is-on' : ''}`}
                                                style={{
                                                    borderColor: isOn ? preset.colorHex : 'rgba(255,255,255,0.16)',
                                                    background: isOn ? preset.bgRgba : 'rgba(255,255,255,0.04)'
                                                }}
                                            >
                                                <span
                                                    className="bmpdf-layer-pill__swatch"
                                                    style={{ background: preset.colorHex }}
                                                />
                                                <span className="bmpdf-layer-pill__name">{preset.label}</span>
                                                <code className="bmpdf-layer-pill__regex">{preset.pattern}</code>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="bmpdf-sandbox__sheet">
                                <div className="bmpdf-sandbox__doc-bar">
                                    <span>Q3_Financial_Audit_2026.pdf — Page 2 of 24</span>
                                    <span>
                                        {activeSandboxLayers.length * 6} active quad-point highlights
                                    </span>
                                </div>
                                <div className="bmpdf-sandbox__body">
                                    <p>
                                        <strong>1.1 Capital Settlement.</strong> Pursuant to{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('clause') ? 'is-coral' : ''}`}>
                                            Section 14.2
                                        </span>
                                        , the Acquiring Party shall remit{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('money') ? 'is-yellow' : ''}`}>
                                            $245,000.00
                                        </span>{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('term') ? 'is-emerald' : ''}`}>
                                            upon closing
                                        </span>.
                                    </p>
                                    <p>
                                        <strong>1.2 Infrastructure Retainer.</strong> Quarterly platform settlement is fixed at{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('money') ? 'is-yellow' : ''}`}>
                                            $48,500.00
                                        </span>{' '}
                                        payable{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('term') ? 'is-emerald' : ''}`}>
                                            net-15
                                        </span>.
                                    </p>
                                    <p>
                                        <strong>1.3 Liability &amp; Holdback.</strong> Under{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('clause') ? 'is-coral' : ''}`}>
                                            Indemnification
                                        </span>{' '}
                                        and{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('clause') ? 'is-coral' : ''}`}>
                                            Force Majeure
                                        </span>{' '}
                                        covenants, an{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('term') ? 'is-emerald' : ''}`}>
                                            escrow reserve
                                        </span>{' '}
                                        of{' '}
                                        <span className={`bmpdf-span ${activeSandboxLayers.includes('money') ? 'is-yellow' : ''}`}>
                                            $18,420.50
                                        </span>{' '}
                                        applies.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* 3 Bold Editorial Pillars — Less Is More, Large Fonts */}
            <section className="bmpdf-pillars">
                <div className="bmpdf-container">
                    <div className="bmpdf-pillar">
                        <div className="bmpdf-pillar__num">01</div>
                        <div className="bmpdf-pillar__content">
                            <h3>One search bar for your entire folder.</h3>
                            <p>
                                Drop in 5 or 500 PDFs. Search literal phrases or regular expressions across every loaded
                                document in parallel—including scanned pages via built-in native OCR.
                            </p>
                        </div>
                    </div>

                    <div className="bmpdf-pillar">
                        <div className="bmpdf-pillar__num">02</div>
                        <div className="bmpdf-pillar__content">
                            <h3>Colour-coded layers that stay organized.</h3>
                            <p>
                                Assign Yellow to dollar figures, Coral to liability clauses, and Emerald to deadlines.
                                Every highlight across every file is indexed in a unified bottom audit table.
                            </p>
                        </div>
                    </div>

                    <div className="bmpdf-pillar">
                        <div className="bmpdf-pillar__num">03</div>
                        <div className="bmpdf-pillar__content">
                            <h3>Zero cloud uploads. Zero monthly rent.</h3>
                            <p>
                                Your legal discovery bundles, financials, and contracts never leave your machine.
                                Built natively in Rust + PDFium for instant page turns and minimal RAM usage.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="bmpdf-pricing">
                <div className="bmpdf-container">
                    <div className="bmpdf-pricing__header">
                        <h2 className="bmpdf-section-title">Pay once. Own it forever.</h2>
                        <p className="bmpdf-section-subtitle">
                            Every license includes all features, unlimited batch regex, native OCR, and 1 year of updates.
                        </p>
                    </div>

                    <div className="bmpdf-pricing__grid">
                        <div className="bmpdf-price-card">
                            <div className="bmpdf-price-card__tier">Personal License</div>
                            <div className="bmpdf-price-card__price">
                                <span className="bmpdf-price-card__amount">$99</span>
                                <span className="bmpdf-price-card__currency">CAD</span>
                            </div>
                            <p className="bmpdf-price-card__desc">
                                Perpetual desktop license for individual researchers, students, and personal workflows.
                            </p>
                            <ul className="bmpdf-price-card__list">
                                <li>Unlimited multi-PDF regex &amp; batch highlighting</li>
                                <li>Custom colour palettes &amp; native PDF export</li>
                                <li>Lifetime license for personal use</li>
                            </ul>
                            <button
                                type="button"
                                className="bmpdf-buy-btn"
                                onClick={() => handlePurchase('prod_TNB8qz8lDP586Q', 'Personal License')}
                                disabled={loading}
                            >
                                {loading ? 'Redirecting to Stripe...' : 'Buy Personal License — $99 CAD'}
                            </button>
                        </div>

                        <div className="bmpdf-price-card bmpdf-price-card--featured">
                            <div className="bmpdf-price-card__badge">MOST POPULAR</div>
                            <div className="bmpdf-price-card__tier">Business License</div>
                            <div className="bmpdf-price-card__price">
                                <span className="bmpdf-price-card__amount">$250</span>
                                <span className="bmpdf-price-card__currency">CAD</span>
                            </div>
                            <p className="bmpdf-price-card__desc">
                                Commercial license for law firms, auditors, accounting teams, and enterprise operations.
                            </p>
                            <ul className="bmpdf-price-card__list">
                                <li>Full commercial &amp; client-work authorization</li>
                                <li>Unlimited multi-PDF regex, OCR &amp; batch export</li>
                                <li>Priority engineering support &amp; tax-compliant receipt</li>
                            </ul>
                            <button
                                type="button"
                                className="bmpdf-buy-btn bmpdf-buy-btn--featured"
                                onClick={() => handlePurchase('prod_TNB8fbpIC0ym3L', 'Business License')}
                                disabled={loading}
                            >
                                {loading ? 'Redirecting to Stripe...' : 'Buy Business License — $250 CAD'}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bmpdf-footer">
                <div className="bmpdf-container bmpdf-footer__inner">
                    <div className="bmpdf-footer__left">
                        <strong>BatchMark PDF</strong> • Engineered by{' '}
                        <a href="https://dforge.ca" target="_blank" rel="noopener noreferrer">
                            Digital Forge Studios
                        </a>
                    </div>
                    <div className="bmpdf-footer__links">
                        <a href="/downloads/BatchMarkPDF-Windows-Setup-v0.1.0.exe">Download Installer</a>
                        <a href="/retrieve">Retrieve License</a>
                        <a href="/support">Customer Support</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default App;
